import {
  faCirclePlus,
  faCopy,
  faEnvelope,
  faFloppyDisk,
  faUserSlash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Flex, Line } from 'components';
import { H2 } from 'components/Typography/Typography';
import { Button, TextField } from 'components/_form';
import { useContactAndSupportsMutations } from 'hooks/useContactAndSupportsMutations';
import { useCompanyContactAndSupportsQuery } from 'hooks/queries/useCompanyContactAndSupportsQuery';
import { useMainCompanyQuery } from 'hooks/queries/useMainCompanyQuery';
import { useAccount } from 'hooks/useAccount';
import { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { theme } from 'styles/theme';
import {
  IContactAndSupportMutationBody,
  ICompanyContactAndSupport
} from 'types/company';

type ContactAndHelpRow = {
  id?: number;
  title: string;
  first_and_last_name: string;
  email_address: string;
  phone: string;
  landline_phone: string;
};

function toMutationPayload(row: ContactAndHelpRow): IContactAndSupportMutationBody {
  return {
    title: row.title ?? '',
    full_name: row.first_and_last_name ?? '',
    email: row.email_address ?? '',
    mobile_phone_number: row.phone ?? '',
    landline_phone_number: row.landline_phone ?? ''
  };
}

function rowFromApi(item: ICompanyContactAndSupport): ContactAndHelpRow {
  return {
    id: item.id,
    title: item.title ?? '',
    first_and_last_name: item.first_and_last_name ?? '',
    email_address: item.email_address ?? '',
    phone: item.phone ?? '',
    landline_phone: item.landline_phone ?? ''
  };
}

export const ContactAndHelpForm = () => {
  const { t } = useTranslation();
  const { isSuperadmin } = useAccount();
  const canEdit = isSuperadmin;
  const { data: mainCompany } = useMainCompanyQuery();
  const companyId = mainCompany?.id ?? '';
  const { data: contactData } = useCompanyContactAndSupportsQuery();
  const { createMutation, updateMutation, deleteMutation } =
    useContactAndSupportsMutations(companyId);

  const { control, register, reset, getValues, watch } = useForm<{
    contacts: ContactAndHelpRow[];
  }>({
    defaultValues: { contacts: [] }
  });

  const { fields, append, remove } = useFieldArray({
    name: 'contacts',
    control
  });

  useEffect(() => {
    if (contactData != null) {
      reset({ contacts: contactData.map(rowFromApi) });
    }
  }, [contactData, reset]);

  const mutationsPending =
    !companyId ||
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  const mutationErrorToast = (error: unknown) => {
    const status =
      typeof error === 'object' && error !== null && 'status' in error
        ? (error as { status: number }).status
        : undefined;
    if (status === 403) {
      toast.error(t('common.permissionDenied'));
    } else {
      toast.error(t('common.anErrorOccurred'));
    }
  };

  const copyFieldValue = async (value: string | undefined) => {
    const v = value?.trim();
    if (!v) return;
    try {
      await navigator.clipboard.writeText(v);
      toast.success(t('common.success'));
    } catch {
      toast.error(t('common.anErrorOccurred'));
    }
  };

  const openMailto = (address: string | undefined) => {
    const m = address?.trim();
    if (!m) return;
    window.open(`mailto:${m}`);
  };

  const handleSaveRow = async (index: number) => {
    if (!companyId) return;
    const row = getValues(`contacts.${index}`);
    try {
      const body = { contact_and_support: toMutationPayload(row) };
      if (row.id != null) {
        await updateMutation.mutateAsync({ id: row.id, body });
      } else {
        await createMutation.mutateAsync(body);
      }
      toast.success(t('common.success'));
    } catch (e) {
      mutationErrorToast(e);
    }
  };

  const handleDeleteRow = async (index: number) => {
    if (!companyId) return;
    const row = getValues(`contacts.${index}`);
    if (row.id != null) {
      if (!window.confirm(t('administration.confirmDeleteContactAndSupport'))) {
        return;
      }
      try {
        await deleteMutation.mutateAsync(row.id);
        toast.success(t('common.success'));
      } catch (e) {
        mutationErrorToast(e);
      }
    } else {
      remove(index);
    }
  };

  const addEmptyRow = () => {
    append({
      title: '',
      first_and_last_name: '',
      email_address: '',
      phone: '',
      landline_phone: ''
    });
  };

  const readOnlyFieldProps = !canEdit
    ? ({
        readOnly: true,
        readOnlyPlain: true,
        labelStyles: {
          color: theme.palette.text.primary,
          fontWeight: 700
        }
      } as const)
    : null;

  return (
    <Flex p={3} flexDirection="column">
      {fields.map((field, index) => (
        <Flex key={field.id} flexDirection="column">
          {canEdit ? (
            <Box mt={index === 0 ? 0 : 2} width="100%">
              <TextField
                {...register(`contacts.${index}.title` as const)}
                label="administration.role"
              />
            </Box>
          ) : (
            <H2
              mt={index === 0 ? 0 : 2}
              variant="h2"
              color="coloured"
              fontWeight={700}
              textTransform="uppercase"
            >
              {watch(`contacts.${index}.title`)?.trim() || '—'}
            </H2>
          )}
          <Box my={3} width="100%">
            <Line style={{ margin: 0 }} />
          </Box>
          <Box width="100%" style={{ overflowX: 'auto' }} pb={1}>
            <Flex
              alignItems="flex-end"
              flexWrap="nowrap"
              gap="12px"
              style={{ minWidth: canEdit ? '720px' : '840px' }}
            >
              <Box flex="1 1 0" minWidth={140} maxWidth={280}>
                <TextField
                  {...register(
                    `contacts.${index}.first_and_last_name` as const
                  )}
                  label="administration.fullname"
                  {...(readOnlyFieldProps ?? {})}
                />
              </Box>

              <Box flex="2 1 0" minWidth={220}>
                <TextField
                  {...register(`contacts.${index}.email_address` as const)}
                  label="administration.emailAddress"
                  {...(readOnlyFieldProps ?? {})}
                  endAffixActions={[
                    {
                      key: 'mailto',
                      ariaLabel: t('administration.emailAddress'),
                      icon: <FontAwesomeIcon icon={faEnvelope} />,
                      onClick: () =>
                        openMailto(watch(`contacts.${index}.email_address`))
                    },
                    ...(!canEdit
                      ? [
                          {
                            key: 'copy-email',
                            ariaLabel: t('common.copy'),
                            icon: <FontAwesomeIcon icon={faCopy} />,
                            onClick: () =>
                              copyFieldValue(
                                watch(`contacts.${index}.email_address`)
                              )
                          }
                        ]
                      : [])
                  ]}
                />
              </Box>

              <Box flex="1 1 0" minWidth={120}>
                <TextField
                  {...register(`contacts.${index}.phone` as const)}
                  label="profile.phone"
                  {...(readOnlyFieldProps ?? {})}
                  endAffixActions={
                    !canEdit
                      ? [
                          {
                            key: 'copy-phone',
                            ariaLabel: t('common.copy'),
                            icon: <FontAwesomeIcon icon={faCopy} />,
                            onClick: () =>
                              copyFieldValue(watch(`contacts.${index}.phone`))
                          }
                        ]
                      : undefined
                  }
                />
              </Box>

              <Box flex="1 1 0" minWidth={120}>
                <TextField
                  {...register(`contacts.${index}.landline_phone` as const)}
                  label="profile.landlinePhone"
                  {...(readOnlyFieldProps ?? {})}
                  endAffixActions={
                    !canEdit
                      ? [
                          {
                            key: 'copy-landline',
                            ariaLabel: t('common.copy'),
                            icon: <FontAwesomeIcon icon={faCopy} />,
                            onClick: () =>
                              copyFieldValue(
                                watch(`contacts.${index}.landline_phone`)
                              )
                          }
                        ]
                      : undefined
                  }
                />
              </Box>

              {canEdit ? (
                <Flex gap="8px" alignItems="center" flexShrink={0}>
                  <Button
                    type="button"
                    variant="eucalyptus"
                    disabled={mutationsPending}
                    minHeight="35px"
                    icon={<FontAwesomeIcon icon={faFloppyDisk} />}
                    onClick={() => handleSaveRow(index)}
                  />
                  <Button
                    type="button"
                    variant="red"
                    disabled={mutationsPending}
                    minHeight="35px"
                    icon={<FontAwesomeIcon icon={faUserSlash} />}
                    onClick={() => handleDeleteRow(index)}
                  />
                </Flex>
              ) : null}
            </Flex>
          </Box>
        </Flex>
      ))}

      {canEdit ? (
        <Flex justifyContent="end" mt={3}>
          <Button
            type="button"
            variant="eucalyptus"
            disabled={mutationsPending}
            onClick={addEmptyRow}
          >
            <Flex>
              <Box mr={2}>
                <FontAwesomeIcon icon={faCirclePlus} />
              </Box>
              {t('administration.addPerson')}
            </Flex>
          </Button>
        </Flex>
      ) : null}
    </Flex>
  );
};
